
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/base/Card';

interface Product {
  name: string;
  title: string;
  description: string;
  icon: string;
  gradient: string;
  path?: string;
}

export const ProductsSection: React.FC = () => {
  const navigate = useNavigate();

  const products: Product[] = [
    {
      name: 'Voice',
      title: 'Whence Voice',
      description: 'Natural language processing with persistent memory and contextual understanding, enabling truly conversational AI interactions.',
      icon: 'ri-mic-line',
      gradient: 'from-primary-500/20 to-blue-500/20',
      path: '/voice',
    },
    {
      name: 'Memory',
      title: 'Whence Memory',
      description: 'Advanced memory systems that retain, organize, and recall information across sessions, creating continuous learning experiences.',
      icon: 'ri-database-line',
      gradient: 'from-purple-500/20 to-pink-500/20',
    },
    {
      name: 'Grid',
      title: 'Whence Grid',
      description: 'Distributed intelligence network that connects multiple AI systems, enabling collaborative reasoning and shared knowledge.',
      icon: 'ri-grid-line',
      gradient: 'from-green-500/20 to-teal-500/20',
    },
    {
      name: 'One',
      title: 'Whence One',
      description: 'Unified AI platform that integrates all Whence modules into a single, coherent artificial general intelligence system.',
      icon: 'ri-focus-line',
      gradient: 'from-orange-500/20 to-red-500/20',
    },
    {
      name: 'Nexus',
      title: 'Whence Nexus',
      description: 'The central hub connecting human intelligence with artificial general intelligence, bridging the gap between minds.',
      icon: 'ri-links-line',
      gradient: 'from-indigo-500/20 to-purple-500/20',
    },
  ];

  const handleProductClick = (product: Product) => {
    if (product.path) {
      navigate(product.path);
    }
  };

  return (
    <section id="products" className="py-24 bg-gradient-to-b from-black to-indigo-950/20 relative">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-indigo-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Product
            <span className="text-transparent bg-clip-text bg-aurora-gradient"> Family</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-400 to-primary-600 mx-auto rounded-full mb-8"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Five interconnected modules working together to create the world's first true AGI platform
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <Card
              key={product.name}
              variant="glass"
              hover={true}
              className={`p-8 bg-gradient-to-br ${product.gradient} border-2 border-white/10 hover:border-primary-400/30 group ${product.path ? 'cursor-pointer' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => handleProductClick(product)}
            >
              <div className="text-center">
                {/* Icon */}
                <div className="w-16 h-16 mx-auto mb-6 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-primary-500/20 transition-all duration-300">
                  <i className={`${product.icon} text-3xl text-primary-400 group-hover:text-primary-300`}></i>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-primary-300 transition-colors duration-300">
                  {product.title}
                </h3>

                {/* Description */}
                <p className="text-gray-300 leading-relaxed text-sm">
                  {product.description}
                </p>

                {/* Status indicator */}
                <div className="mt-6 flex items-center justify-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-primary-400 text-sm font-medium">
                    {product.path ? 'Learn More →' : 'In Development'}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Integration visualization */}
        <div className="mt-16 text-center">
          <Card variant="glass" className="p-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center mr-4">
                <i className="ri-puzzle-line text-2xl text-primary-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white">Seamless Integration</h3>
            </div>
            <p className="text-gray-300 text-lg leading-relaxed">
              Each module is designed to work independently or as part of the complete Whence ecosystem. 
              Together, they form a comprehensive AGI platform that adapts, learns, and evolves with your needs.
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};
